import { Inject, Injectable } from '@angular/core';
import {
  IAvailableState,
  IAvailableStatus,
  IUnavailableReason,
  TAvailabilityStatus,
  TAvailabilityView,
} from '@x/ecommerce-shop/app/core/availability/availability';
import { IShopConfig, SHOP_CONFIG } from '@x/ecommerce-shop/config/shop.config';
import { Maybe, ShopProductUnavailableReason } from '@x/schemas/ecommerce';
import { DateTime } from 'luxon';

/**
 * Configuration for availability status
 */

export type TAvailabilityStatusConfig = Record<
  Exclude<IAvailableStatus, 'DEFAULT_AVAILABLE_DATE'>,
  IAvailabilityThresholds
>;
interface IAvailabilityThresholds {
  minDays?: number;
  maxDays?: number;
  maxQuantity?: number;
}

/**
 * Service responsible for determining product availability status and formatting
 * availability information for display to users.
 */
@Injectable({ providedIn: 'root' })
export class AvailabilityService {
  constructor(@Inject(SHOP_CONFIG) private shopConfig: IShopConfig) {}

  // Constants for availability calculations
  private readonly AVAILABILITY_THRESHOLDS =
    this.shopConfig.features.availability.availabilityStatus;

  // Reason codes that indicate a product is unlisted
  private readonly UNLISTED_REASON_CODES: Maybe<ShopProductUnavailableReason>[] = [
    ShopProductUnavailableReason.Unlisted,
    ShopProductUnavailableReason.UnlistedRegion,
  ];

  // Reason codes that indicate a product is out of stock
  private readonly OUT_OF_STOCK_REASON_CODES: Maybe<ShopProductUnavailableReason>[] = [
    ShopProductUnavailableReason.NoStock,
  ];

  /**
   * Main public method to generate a complete availability view for a product
   *
   * @param availability Product availability data
   * @returns Formatted availability view for display
   */
  getAvailabilityView(availability: {
    isAvailable: boolean;
    showDefault?: boolean;
    minAvailableDate?: string | null;
    maxAvailableDate?: string | null;
    maxAvailableQuantity?: number | null;
    unavailableReason?: ShopProductUnavailableReason | null;
    availabilityRegionId?: number | null;
    availableRegionsCount?: number | null;
  }): TAvailabilityView {
    const {
      isAvailable,
      minAvailableDate,
      unavailableReason,
      availabilityRegionId,
      availableRegionsCount,
      maxAvailableDate,
      maxAvailableQuantity,
      showDefault,
    } = availability;

    return {
      status: this.determineAvailabilityStatus(
        isAvailable,
        unavailableReason,
        availabilityRegionId,
        availableRegionsCount,
      ),
      availableState: this.calculateAvailableState(
        isAvailable,
        minAvailableDate,
        maxAvailableDate,
        maxAvailableQuantity,
        showDefault,
      ),
      unavailableReason: this.formatUnavailableReason(unavailableReason),
    };
  }

  /**
   * Determines the high-level availability status of a product
   */
  private determineAvailabilityStatus(
    isAvailable: boolean,
    unavailableReason: ShopProductUnavailableReason | undefined | null,
    availabilityRegionId: number | undefined | null,
    availableRegionsCount: number | undefined | null,
  ): TAvailabilityStatus {
    // Product is available in some regions but not the user's region
    if (
      isAvailable &&
      availableRegionsCount &&
      availableRegionsCount > 0 &&
      !availabilityRegionId
    ) {
      return 'LIMITED';
    }

    // Product is not available
    if (!isAvailable) {
      // Check if product is unlisted in the user's region
      if (this.UNLISTED_REASON_CODES.includes(unavailableReason)) {
        return 'UNLISTED';
      }

      // Check if product is out of stock
      if (this.OUT_OF_STOCK_REASON_CODES.includes(unavailableReason)) {
        return 'OUT_OF_STOCK';
      }

      // Default unavailable status
      return 'UNAVAILABLE';
    }

    // Product is available
    return 'AVAILABLE';
  }

  /**
   * Calculates the detailed availability state for available products
   */
  private calculateAvailableState(
    isAvailable: boolean,
    minAvailableDate: string | undefined | null,
    maxAvailableDate: string | undefined | null,
    nextAvailableQuantity: number | undefined | null,
    showDefault: boolean = false,
  ): IAvailableState | undefined {
    // Don't calculate state for unavailable products
    if (!isAvailable) {
      return undefined;
    }

    try {
      const now = DateTime.now();
      const minAvailableDateTime = this.parseDate(minAvailableDate);
      const maxAvailableDateTime = this.parseDate(maxAvailableDate);

      // No dates available, can't determine state
      if (!minAvailableDateTime && !maxAvailableDateTime) {
        return undefined;
      }

      // Calculate days until product is available
      let availableState: IAvailableState | undefined;

      if (minAvailableDateTime) {
        const daysUntilAvailable = minAvailableDateTime.diff(now, 'days').days;
        const formattedDate = minAvailableDateTime.toFormat('d MMM');

        // Default availability state with date
        if (showDefault) {
          availableState = {
            status: 'DEFAULT_AVAILABLE_DATE',
            description: `Available ${formattedDate}`,
          };
        }

        // Available soon (more than 3 days)
        if (daysUntilAvailable > this.AVAILABILITY_THRESHOLDS.AVAILABLE_SOON.minDays!) {
          availableState = {
            status: 'AVAILABLE_SOON',
            description: `Available ${formattedDate}`,
          };
        }

        // Pre-order (more than 14 days)
        if (daysUntilAvailable > this.AVAILABILITY_THRESHOLDS.PRE_ORDER.minDays!) {
          availableState = {
            status: 'PRE_ORDER',
            description: `Pre-order ${formattedDate}`,
          };
        }

        // Low stock urgency (available soon with limited quantity)
        const thresholds = this.AVAILABILITY_THRESHOLDS.LOW_STOCK_URGENCY;
        if (
          daysUntilAvailable <= thresholds.maxDays! &&
          nextAvailableQuantity !== null &&
          nextAvailableQuantity !== undefined &&
          nextAvailableQuantity < thresholds.maxQuantity!
        ) {
          availableState = {
            status: 'LOW_STOCK_URGENCY',
            description: `${nextAvailableQuantity} available`,
          };
        }
      }

      // Check for last chance availability
      if (maxAvailableDateTime) {
        const daysUntilUnavailable = maxAvailableDateTime.diff(now, 'days').days;
        const lastChanceThreshold = this.AVAILABILITY_THRESHOLDS.LAST_CHANCE_AVAILABILITY.maxDays!;

        if (daysUntilUnavailable < lastChanceThreshold) {
          availableState = {
            status: 'LAST_CHANCE_AVAILABILITY',
            description: `Available until ${maxAvailableDateTime.toFormat('d MMM')}`,
          };
        }
      }

      return availableState;
    } catch (error) {
      console.error('Error calculating availability state:', error);
      return undefined;
    }
  }

  /**
   * Formats unavailability reasons into user-friendly messages
   */
  private formatUnavailableReason(
    reasonCode: ShopProductUnavailableReason | undefined | null,
  ): IUnavailableReason | undefined | null {
    if (!reasonCode) {
      return undefined;
    }

    switch (reasonCode) {
      // Region-based unavailability
      case ShopProductUnavailableReason.Unlisted:
      case ShopProductUnavailableReason.UnlistedRegion:
        return {
          label: 'Unavailable',
          description: 'Product not available in your delivery area',
        };

      // Stock-based unavailability
      case ShopProductUnavailableReason.NoShippingMethod:
      case ShopProductUnavailableReason.NoShippingSlot:
      case ShopProductUnavailableReason.NoSlotCapacity:
      case ShopProductUnavailableReason.NoStock:
        return {
          label: 'Out of Stock',
        };

      // General unavailability
      case ShopProductUnavailableReason.Unavailable:
      case ShopProductUnavailableReason.Unknown:
      default:
        return {
          label: 'Unavailable',
          description: 'Product not currently available',
        };
    }
  }

  /**
   * Helper method to safely parse ISO date strings
   */
  private parseDate(dateString: string | undefined | null): DateTime | null {
    if (!dateString) {
      return null;
    }

    try {
      return DateTime.fromISO(dateString);
    } catch (error) {
      console.error(`Error parsing date: ${dateString}`, error);
      return null;
    }
  }
}
